import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Card } from "../../../components/ui/card";
import { testPlanService } from "../../../services/testPlanService";
import { TestPlanHeader } from "../components/TestPlanHeader";
import { TestPlanFilters } from "../components/TestPlanFilters";
import { TestPlanBulkBar } from "../components/TestPlanBulkBar";
import { TestPlanTable } from "../components/TestPlanTable";
import { TestPlanEmptyState } from "../components/TestPlanEmptyState";
import { useCan } from "../../../hooks/useCan";
import TableSkeleton from "../../../components/skeletons/TableSkeleton";

const defaultFilters = {
  assignTo: "all",
  sort: "most_recent",
  status: "all",
  search: "",
};

function mapApiPlanToRow(p) {
  const count = typeof p.testRunCount === "number" ? p.testRunCount : 0;
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    reference: Array.isArray(p.reference) ? p.reference : [],
    startDate: p.start_date,
    endDate: p.end_date,
    status: p.status,
    testRunLabels: count > 0 ? [`${count} run${count === 1 ? "" : "s"}`] : [],
  };
}

function sortRows(rows, sortKey) {
  const copy = [...rows];
  if (sortKey === "name") {
    copy.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  } else if (sortKey === "status") {
    copy.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
  } else {
    copy.sort(
      (a, b) =>
        new Date(b.startDate || b.endDate || 0) -
        new Date(a.startDate || a.endDate || 0),
    );
  }
  return copy;
}

/**
 * Test Plans listing — loads from POST testplan/list (real API).
 */
const TestPlanList = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get("project_id");
  const projectName = searchParams.get("project_name") || "Project";
  const clientIdQs = searchParams.get("client_id");

  const [filters, setFilters] = useState(defaultFilters);
  const [apiPlans, setApiPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlans, setSelectedPlans] = useState([]);

  const { can: canAdd } = useCan("runs_plan", "add_edit");

  const setFilter = useCallback((key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
  }, []);

  const loadPlans = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      setApiPlans([]);
      return;
    }
    setLoading(true);
    try {
      const reqFilters = {};
      if (filters.status !== "all") reqFilters.status = filters.status;
      if (filters.assignTo === "me") reqFilters.assigned_to = "me";
      else if (filters.assignTo !== "all")
        reqFilters.assigned_to = filters.assignTo;
      if (filters.search?.trim()) reqFilters.search = filters.search.trim();

      const res = await testPlanService.list(projectId, reqFilters);
      // Backend: { success, message, data: { test_plans, pagination } }; some clients may unwrap differently
      const raw =
        res?.data?.test_plans ??
        res?.data?.data?.test_plans ??
        res?.test_plans ??
        [];
      setApiPlans(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error("Error loading test plans:", error);
      toast.error(error.response?.data?.message || "Failed to load test plans");
      setApiPlans([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, filters.status, filters.assignTo, filters.search]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const tableRows = useMemo(() => {
    const mapped = apiPlans.map(mapApiPlanToRow);
    return sortRows(mapped, filters.sort);
  }, [apiPlans, filters.sort]);

  const hasActiveFilters = useMemo(
    () =>
      filters.assignTo !== "all" ||
      filters.status !== "all" ||
      Boolean((filters.search || "").trim()),
    [filters],
  );

  const showFullPageEmpty =
    !loading && apiPlans.length === 0 && !hasActiveFilters;

  const qsExtras = useMemo(() => {
    const q = new URLSearchParams();
    q.set("project_id", projectId || "");
    q.set("project_name", projectName);
    if (clientIdQs) q.set("client_id", clientIdQs);
    return q.toString();
  }, [projectId, projectName, clientIdQs]);

  const goCreate = useCallback(() => {
    navigate(`/test-plans/create?${qsExtras}`);
  }, [navigate, qsExtras]);

  const goEdit = useCallback(
    (row) => {
      navigate(`/test-plans/edit/${encodeURIComponent(row.id)}?${qsExtras}`);
    },
    [navigate, qsExtras],
  );

  const allIds = tableRows.map((p) => p.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selectedPlans.includes(id));
  const someSelected = selectedPlans.some((id) => allIds.includes(id));

  const toggleSelect = (id) => {
    setSelectedPlans((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllVisible = () => {
    const all =
      allIds.length > 0 && allIds.every((id) => selectedPlans.includes(id));
    setSelectedPlans(all ? [] : allIds);
  };

  const handleArchive = async (plan) => {
    try {
      await testPlanService.archive(plan.id, projectId);
      toast.success("Test plan archived");
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to archive");
    }
  };

  const handleRestore = async (plan) => {
    try {
      await testPlanService.restore(plan.id, projectId);
      toast.success("Test plan restored");
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to restore");
    }
  };

  const handleComplete = async (plan) => {
    try {
      await testPlanService.complete(plan.id, projectId);
      toast.success("Test plan completed");
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to complete");
    }
  };

  const handleDelete = async (plan) => {
    if (
      !window.confirm(
        `Delete test plan "${plan.name}"? Only completed plans can be deleted.`,
      )
    )
      return;
    try {
      await testPlanService.delete(plan.id, projectId);
      toast.success("Test plan deleted");
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete");
    }
  };

  const handleBulkArchive = async () => {
    if (selectedPlans.length === 0) return;
    try {
      await testPlanService.bulkArchive(selectedPlans, projectId);
      toast.success("Plans archived");
      setSelectedPlans([]);
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Bulk archive failed");
    }
  };

  const handleBulkRestore = async () => {
    if (selectedPlans.length === 0) return;
    try {
      await testPlanService.bulkRestore(selectedPlans, projectId);
      toast.success("Plans restored");
      setSelectedPlans([]);
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Bulk restore failed");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPlans.length === 0) return;
    if (!window.confirm(`Delete ${selectedPlans.length} completed plan(s)?`))
      return;
    try {
      await testPlanService.bulkDelete(selectedPlans, projectId);
      toast.success("Plans deleted");
      setSelectedPlans([]);
      loadPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || "Bulk delete failed");
    }
  };

  if (!projectId) {
    return (
      <div className="p-6" data-testid="test-plan-list-missing-project">
        <p className="font-semibold text-rose-600">
          Project ID is required. Open Test Plans from a project.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="test-plan-list-page">
      <nav className="mb-2 flex flex-wrap items-center gap-2 text-sm text-text-secondary">
        <button
          type="button"
          className="transition-colors hover:text-violet-600"
          onClick={() => navigate("/projects")}
        >
          Projects
        </button>
        <span>/</span>
        <button
          type="button"
          className="transition-colors hover:text-violet-600"
          onClick={() =>
            navigate(
              `/test-runs?project_id=${projectId}&project_name=${encodeURIComponent(projectName)}`,
            )
          }
        >
          {projectName}
        </button>
        <span>/</span>
        <span className="text-text-primary">Test Plans</span>
      </nav>

      <TestPlanHeader onAddTestPlan={goCreate} canAdd={canAdd} />

      {loading && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <TableSkeleton rows={8} columns={5} />
        </div>
      )}

      {!loading && showFullPageEmpty ? (
        <TestPlanEmptyState onAddTestPlan={goCreate} canAdd={canAdd} />
      ) : !loading ? (
        <>
          <Card className="rounded-xl border border-gray-200 p-4 shadow-sm">
            <TestPlanFilters filters={filters} onFilterChange={setFilter} />
          </Card>

          <Card className="rounded-xl border border-gray-200 p-4 shadow-sm">
            <TestPlanBulkBar
              selectedCount={selectedPlans.length}
              onBulkDelete={handleBulkDelete}
              onBulkArchive={handleBulkArchive}
              onBulkRestore={handleBulkRestore}
              disabled={false}
            />
            {tableRows.length === 0 ? (
              <div className="py-16 text-center">
                <p className="text-base font-semibold text-text-primary">
                  No test plans found
                </p>
                <p className="mt-2 text-sm text-text-secondary">
                  Adjust filters or create a new test plan.
                </p>
                {canAdd && (
                  <button
                    type="button"
                    className="mt-6 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
                    onClick={goCreate}
                  >
                    + Add Test Plans
                  </button>
                )}
              </div>
            ) : (
              <TestPlanTable
                rows={tableRows}
                selectedPlans={selectedPlans}
                onToggleRow={toggleSelect}
                onSelectAll={selectAllVisible}
                allSelected={allSelected}
                someSelected={someSelected}
                onRun={(row) =>
                  navigate(
                    `/test-runs/new?project_id=${projectId}&test_plan_id=${encodeURIComponent(row.id)}`,
                  )
                }
                onEdit={goEdit}
                onComplete={handleComplete}
                onArchive={handleArchive}
                onClone={(row) =>
                  navigate(
                    `/test-plans/create?${qsExtras}&clone_id=${encodeURIComponent(row.id)}`,
                  )
                }
                onDelete={handleDelete}
                onRestore={handleRestore}
                onReferenceClick={() => {}}
                onTestRunsClick={(row) => goEdit(row)}
              />
            )}
          </Card>

          <div
            className="flex justify-center rounded-lg border border-dashed border-gray-200 py-3 text-sm text-text-secondary"
            data-testid="test-plan-pagination-placeholder"
          >
            Pagination — connect when API supports paging
          </div>
        </>
      ) : null}
    </div>
  );
};

export default TestPlanList;
